import { z } from "zod";
import { twinProductCoverageSchema } from "./session-contracts.js";
import { authoringCreatedBySchema, authoringPersistedIdSchema, authoringSlugSchema } from "./authoring-shared.js";
import { createSkillInputSchema, patchSkillVersionInputSchema, skillCapabilitySchema, } from "./authoring-skills.js";
import { patchChannelViewInputSchema } from "./channels.js";
import { channelDatasetFreshnessDeclarationSchema } from "./channel-data-trace.js";
const jsonObjectSchema = z.record(z.string(), z.unknown());
const boundedTextSchema = z.string().trim().min(1).max(4000);
const fieldPathSchema = z
    .string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)*$/, "Field path must use dotted identifier segments");
const relationNameSchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z_][A-Za-z0-9_]*(\.[A-Za-z_][A-Za-z0-9_]*)?$/, "Relation name must be schema.table or table");
const jsonScalarSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
]);
const sqlIdentifierSchema = z
    .string()
    .trim()
    .min(1)
    .max(63)
    .regex(/^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/u, "Expected snake_case SQL identifier");
export const convergenceDomainStatusSchema = z.enum(["active", "archived"]);
export const semanticViewStatusSchema = z.enum(["active", "archived"]);
export const semanticViewVersionStatusSchema = z.enum(["draft", "published", "archived"]);
export const semanticViewKindSchema = z.enum([
    "business_logic", "data_dictionary", "metric_definition", "source_profile", "validation_rubric",
]);
export const semanticViewGrainSchema = z.enum([
    "row", "event", "document", "booking", "invoice", "journal_line",
    "accounting_period", "artist", "promoter", "organisation", "metric",
]);
export const semanticViewFilterOperatorSchema = z.enum([
    "equals", "not_equals", "in", "not_in", "is_present", "is_absent", "gte", "lte", "between",
]);
export const semanticViewRequiredFilterSchema = z.object({
    field: fieldPathSchema,
    operator: semanticViewFilterOperatorSchema.default("equals"),
    value: jsonScalarSchema.optional(),
    values: z.array(jsonScalarSchema).min(1).max(128).optional(),
    reason: z.string().trim().min(1).max(1000),
});
// Business-semantic tag distinguishing e.g. "amount that's a percent" from
// "amount that's currency" -- a distinction the raw SQL/warehouse type alone
// can't make. Drives the static valueKind -> channel field_type mapping below
// Existing measures/dimensions predate this field and
// default to "text", which is a conservative, non-destructive default (never
// mis-renders a number as broken; worst case a channel column looks plainer
// than it could).
export const semanticViewValueKindSchema = z.enum([
    "identifier", "count", "currency", "percent", "ratio",
    "text", "date", "datetime", "boolean", "reference", "json",
]);
export const semanticViewMeasureSchema = z.object({
    name: authoringSlugSchema,
    label: z.string().trim().min(1).max(160),
    field: fieldPathSchema.optional(),
    expression: z.string().trim().min(1).max(1000).optional(),
    description: z.string().trim().max(1000).optional(),
    valueKind: semanticViewValueKindSchema.default("text"),
});
export const semanticViewDimensionSchema = z.object({
    name: authoringSlugSchema,
    label: z.string().trim().min(1).max(160),
    field: fieldPathSchema,
    description: z.string().trim().max(1000).optional(),
    valueKind: semanticViewValueKindSchema.default("text"),
});
/**
 * Static valueKind -> channel field_type/config mapping.
 * Deliberately not runtime-configurable: once valueKind is set on a measure/
 * dimension, there is no remaining ambiguity to resolve.
 */
export function mapSemanticValueKindToChannelFieldType(valueKind, options = {}) {
    switch (valueKind) {
        case "identifier":
            return { fieldType: "text", config: {} };
        case "count":
            return { fieldType: "number", config: { precision: 0 } };
        case "currency":
            return {
                fieldType: "currency",
                config: { symbol: options.currencySymbol ?? "GBP", precision: 2 },
            };
        case "percent":
            return { fieldType: "percent", config: { precision: 1 } };
        case "ratio":
            return { fieldType: "number", config: { precision: 2 } };
        case "text":
            return { fieldType: "text", config: {} };
        case "date":
            return { fieldType: "date", config: {} };
        case "datetime":
            return { fieldType: "datetime", config: {} };
        case "boolean":
            return { fieldType: "boolean", config: {} };
        case "reference":
            if (!options.targetObjectTypeId) {
                throw new Error('valueKind "reference" requires an explicit targetObjectTypeId');
            }
            return { fieldType: "reference", config: { targetObjectTypeId: options.targetObjectTypeId } };
        case "json":
            return { fieldType: "json", config: {} };
    }
}
const channelSchemaNameSchema = z.enum(["mlx_curated", "connector_warehouse"]);
// Explicit, proposal-supplied column declaration -- never an auto-detected
// mapping (Gap 2a). targetObjectTypeId is required when valueKind is
// "reference"; currencySymbol is only meaningful for valueKind "currency" and
// defaults to "GBP" if omitted.
export const channelDataProductColumnSchema = z.object({
    sourceColumn: sqlIdentifierSchema,
    channelFieldSlug: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .regex(/^[A-Za-z_][A-Za-z0-9_]*$/u, "Channel field slug must be a valid identifier"),
    label: z.string().trim().min(1).max(160),
    valueKind: semanticViewValueKindSchema,
    required: z.boolean().default(false),
    targetObjectTypeId: authoringPersistedIdSchema.optional(),
    currencySymbol: z.string().trim().min(1).max(8).optional(),
}).strict();
const channelDataProductSourceFieldsSchema = z.object({
    schemaName: channelSchemaNameSchema,
    relationName: sqlIdentifierSchema,
    sourceSystem: z.string().trim().min(1).max(120),
    externalIdColumn: sqlIdentifierSchema,
    titleColumn: sqlIdentifierSchema.optional(),
    columns: z.array(channelDataProductColumnSchema).min(1).max(80),
    freshness: channelDatasetFreshnessDeclarationSchema.optional(),
});
// create_channel_data_product: fires when no channel/object-type/dataset
// binding exists at all for the relevant view. Reuse is only ever by explicit
// existingChannelId -- never inferred by name-matching against existing
// channels. channelName is required when existingChannelId is omitted.
export const createChannelDataProductPatchSchema = channelDataProductSourceFieldsSchema
    .extend({
    existingChannelId: authoringPersistedIdSchema.optional(),
    channelName: z.string().trim().min(1).max(200).optional(),
    objectTypeName: z.string().trim().min(1).max(200),
})
    .strict()
    .superRefine((patch, ctx) => {
    if (!patch.existingChannelId && !patch.channelName) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["channelName"],
            message: "channelName is required when existingChannelId is omitted",
        });
    }
});
// bind_curated_view_to_channel_dataset: fires on its own when a channel/object
// type already exists but this specific view isn't yet bound to it. Both ids
// must already exist -- this operation never creates a new object type.
export const bindCuratedViewToChannelDatasetPatchSchema = channelDataProductSourceFieldsSchema
    .extend({
    channelId: authoringPersistedIdSchema,
    objectTypeId: authoringPersistedIdSchema,
})
    .strict();
// patch_channel_view_definition: fires only when a binding already exists
// whose rendered saved view is wrong. Reused verbatim from the existing
// channel-view route's own patch schema.
export const patchChannelViewDefinitionProposalPatchSchema = patchChannelViewInputSchema;
const twinDefinitionStatusSchema = z.enum(["hypothesis", "verified", "adjudicated", "archived"]);
export const setSourceRecordDesignationProposalPatchSchema = z.object({
    factKind: z.string().trim().min(1).max(160),
    source: z.string().trim().min(1).max(120),
    role: z.enum(["authoritative", "corroborating"]),
    status: z.enum(["verified", "adjudicated", "archived"]),
    rationale: z.string().trim().min(1).max(8000),
    provenance: jsonObjectSchema.default({}),
    evidenceHash: z.string().regex(/^[0-9a-f]{64}$/u),
}).strict();
export const createRelationshipRuleProposalPatchSchema = z.object({
    id: authoringPersistedIdSchema.optional(),
    slug: authoringSlugSchema,
    version: z.number().int().positive(),
    status: twinDefinitionStatusSchema.default("hypothesis"),
    source: z.string().trim().min(1).max(120),
    entity: z.string().trim().min(1).max(120),
    // Array-expansion field for item.* rules. Rejecting it here while the twin
    // validated it upstream silently mutilated expanded rules at apply time.
    expand: z.string().trim().min(1).max(200).nullish(),
    quantification: z.literal("open_world"),
    predicate: jsonObjectSchema,
    emits: jsonObjectSchema,
    provenance: jsonObjectSchema.default({}),
}).strict();
export const publishRelationshipRuleProposalPatchSchema = z.object({
    relationshipRuleId: authoringPersistedIdSchema,
    status: twinDefinitionStatusSchema.default("verified"),
}).strict();
const twinProductRoutingBindingSchema = z.object({
    pluginSlug: authoringSlugSchema,
    baseSkillSlug: authoringSlugSchema,
    skillSlug: authoringSlugSchema,
    matchMode: z.enum(["always", "prompt", "explicit"]),
    priority: z.number().int().min(0).max(1000),
    triggerPhrases: z.array(z.string().trim().min(1).max(120)).max(64),
}).strict();
const twinProductProviderCompletionSchema = z.object({
    pluginSlug: authoringSlugSchema,
    skillSlug: authoringSlugSchema,
}).strict();
export const twinProductCompositionMemberSchema = z.object({
    productSlug: authoringSlugSchema,
    productVersion: z.number().int().positive(),
    skillSlug: authoringSlugSchema,
    section: z.string().trim().min(1).max(160),
    publicationAuthority: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("self") }).strict(),
        z.object({
            kind: z.literal("exact"),
            publicationVersionId: authoringPersistedIdSchema,
        }).strict(),
    ]),
}).strict();
export const twinProductCompositionPlanSchema = z.object({
    slug: authoringSlugSchema,
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(1200),
    routingDescription: z.string().trim().min(1).max(600),
    carrierSkillSlug: authoringSlugSchema,
    members: z.array(twinProductCompositionMemberSchema).min(2).max(7),
}).strict().superRefine((plan, context) => {
    const members = new Set();
    const skillSlugs = new Set();
    let selfMembers = 0;
    for (const [index, member] of plan.members.entries()) {
        const key = `${member.productSlug}\u0000${member.productVersion}\u0000${member.skillSlug}`;
        if (members.has(key)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["members", index],
                message: "Composition plan members must be unique.",
            });
        }
        members.add(key);
        if (skillSlugs.has(member.skillSlug)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["members", index, "skillSlug"],
                message: "Composition plan member skill slugs must be unique.",
            });
        }
        skillSlugs.add(member.skillSlug);
        if (member.publicationAuthority.kind === "self") {
            selfMembers += 1;
            if (member.skillSlug !== plan.carrierSkillSlug) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["members", index, "publicationAuthority"],
                    message: "Only the composition carrier skill can use self publication authority.",
                });
            }
        }
    }
    if (!skillSlugs.has(plan.carrierSkillSlug)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["carrierSkillSlug"],
            message: "Composition carrier must also be an ordered plan member.",
        });
    }
    if (selfMembers !== 1) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["members"],
            message: "Composition plans require exactly one self-authoritative carrier member.",
        });
    }
});
export const twinOverviewPlanFileSchema = z.object({
    schemaVersion: z.literal("mlx.twin-overview-plan.v1"),
    organisationId: authoringPersistedIdSchema,
    anchorProduct: z.object({
        slug: authoringSlugSchema,
        productVersion: z.number().int().positive(),
    }).strict(),
    plan: twinProductCompositionPlanSchema,
}).strict().superRefine((file, context) => {
    const selfMember = file.plan.members.find((member) => member.publicationAuthority.kind === "self");
    if (selfMember?.productSlug !== file.anchorProduct.slug
        || selfMember.productVersion !== file.anchorProduct.productVersion) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["anchorProduct"],
            message: "Overview plan anchor must identify the self-authoritative carrier product.",
        });
    }
});
export const twinEvaluationFactSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("text"),
        label: z.string().trim().min(1).max(240),
        value: z.string().trim().min(1).max(1000),
        match: z.enum(["token", "phrase"]).default("phrase"),
    }).strict(),
    z.object({
        kind: z.literal("absent_text"),
        label: z.string().trim().min(1).max(240),
        value: z.string().trim().min(1).max(1000),
        match: z.enum(["token", "phrase"]).default("phrase"),
    }).strict(),
    z.object({
        kind: z.literal("one_of_text"),
        label: z.string().trim().min(1).max(240),
        values: z.array(z.string().trim().min(1).max(1000)).min(2).max(100),
        match: z.enum(["token", "phrase"]).default("phrase"),
    }).strict(),
    z.object({
        kind: z.literal("number"),
        label: z.string().trim().min(1).max(240),
        value: z.number().finite(),
        tolerance: z.number().finite().nonnegative().optional(),
        anchors: z.array(z.string().trim().min(1).max(240)).min(1).max(8).optional(),
    }).strict(),
    z.object({
        kind: z.literal("evidence_number"),
        label: z.string().trim().min(1).max(240),
        valueColumn: z.string().trim().regex(/^[A-Za-z_][A-Za-z0-9_]*$/u),
        valueColumnAliases: z.array(z.string().trim().regex(/^[A-Za-z_][A-Za-z0-9_]*$/u)).min(1).max(8).optional(),
        rowSelector: z.object({
            column: z.string().trim().regex(/^[A-Za-z_][A-Za-z0-9_]*$/u),
            columnAliases: z.array(z.string().trim().regex(/^[A-Za-z_][A-Za-z0-9_]*$/u)).min(1).max(8).optional(),
            equals: z.union([z.string().max(1000), z.number().finite(), z.boolean()]),
        }).strict().optional(),
        tolerance: z.number().finite().nonnegative().optional(),
        anchors: z.array(z.string().trim().min(1).max(240)).min(1).max(8).optional(),
    }).strict(),
]).superRefine((fact, context) => {
    if (fact.kind === "evidence_number") {
        if (fact.valueColumnAliases?.includes(fact.valueColumn)) {
            context.addIssue({
                code: "custom",
                path: ["valueColumnAliases"],
                message: "Evidence value aliases must not repeat the canonical column.",
            });
        }
        if (fact.valueColumnAliases && new Set(fact.valueColumnAliases).size !== fact.valueColumnAliases.length) {
            context.addIssue({
                code: "custom",
                path: ["valueColumnAliases"],
                message: "Evidence value aliases must be unique.",
            });
        }
        if (fact.rowSelector?.columnAliases?.includes(fact.rowSelector.column)) {
            context.addIssue({
                code: "custom",
                path: ["rowSelector", "columnAliases"],
                message: "Evidence selector aliases must not repeat the canonical column.",
            });
        }
        if (fact.rowSelector?.columnAliases &&
            new Set(fact.rowSelector.columnAliases).size !== fact.rowSelector.columnAliases.length) {
            context.addIssue({
                code: "custom",
                path: ["rowSelector", "columnAliases"],
                message: "Evidence selector aliases must be unique.",
            });
        }
    }
});
export const twinProductEvaluationCaseSchema = z.object({
    id: authoringSlugSchema,
    prompt: z.string().trim().min(1).max(8000),
    requestedCapabilities: z.array(skillCapabilitySchema).min(1).max(8).default(["readonly-data"]),
    expectedFacts: z.array(twinEvaluationFactSchema).max(100).default([]),
    qualitativeCriteria: z.array(z.never()).max(0).default([]),
    requireCitation: z.boolean().optional(),
    requireProductEvidence: z.boolean().optional(),
    maxToolCalls: z.number().int().positive().optional(),
    semanticOutcome: z.object({
        kind: z.enum(["present", "certified_zero", "unsupported"]),
        coverageKeys: z.array(z.string().trim().regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/u)).min(1).max(20),
        unsupportedErrorCode: z.literal("session_contract_incomplete").optional(),
        unsupportedResponseMode: z.enum(["fail_closed", "bounded_explanation"]).optional(),
    }).strict().optional(),
}).strict().superRefine((evaluationCase, context) => {
    if (evaluationCase.expectedFacts.length === 0 &&
        evaluationCase.semanticOutcome?.kind !== "unsupported") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expectedFacts"],
            message: "Evaluation cases require deterministic expected facts.",
        });
    }
    if (evaluationCase.semanticOutcome?.kind === "unsupported" &&
        evaluationCase.expectedFacts.length > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["expectedFacts"],
            message: "Unsupported cases are proved by fail-closed run state, not answer text.",
        });
    }
    if (evaluationCase.semanticOutcome?.unsupportedErrorCode &&
        evaluationCase.semanticOutcome.kind !== "unsupported") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["semanticOutcome", "unsupportedErrorCode"],
            message: "unsupportedErrorCode is valid only for unsupported outcomes.",
        });
    }
    if (evaluationCase.semanticOutcome?.unsupportedResponseMode &&
        evaluationCase.semanticOutcome.kind !== "unsupported") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["semanticOutcome", "unsupportedResponseMode"],
            message: "unsupportedResponseMode is valid only for unsupported outcomes.",
        });
    }
    if (evaluationCase.semanticOutcome?.unsupportedResponseMode === "bounded_explanation" &&
        evaluationCase.semanticOutcome.unsupportedErrorCode) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["semanticOutcome", "unsupportedErrorCode"],
            message: "Bounded explanations complete normally and cannot declare an unsupported error code.",
        });
    }
});
export const twinProductEvaluationSuiteSchema = z.object({
    id: authoringSlugSchema,
    version: z.number().int().positive(),
    name: z.string().trim().min(1).max(240),
    description: z.string().trim().min(1).max(2000),
    cases: z.array(twinProductEvaluationCaseSchema).min(1).max(200),
}).strict().superRefine((suite, context) => {
    const seen = new Set();
    for (const [index, evaluationCase] of suite.cases.entries()) {
        if (seen.has(evaluationCase.id)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["cases", index, "id"],
                message: `Duplicate evaluation case ${evaluationCase.id}.`,
            });
        }
        seen.add(evaluationCase.id);
    }
});
export const twinProductEvaluationContractSchema = z.object({
    engineVersion: z.number().int().positive(),
    requireCitation: z.boolean().default(true),
    requireProductEvidence: z.boolean().default(true),
    maxToolCalls: z.number().int().positive().optional(),
    suites: z.array(twinProductEvaluationSuiteSchema).min(1).max(100),
}).strict().superRefine((evaluation, context) => {
    const seen = new Set();
    for (const [index, suite] of evaluation.suites.entries()) {
        const key = `${suite.id}@${suite.version}`;
        if (seen.has(key)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["suites", index],
                message: `Duplicate evaluation suite version ${key}.`,
            });
        }
        seen.add(key);
    }
});
export const twinPublicAccessSchema = z.object({
    enabled: z.literal(true),
    dataPolicy: z.literal("aggregate_non_personal"),
    approvedBy: z.string().trim().min(1).max(200),
    approvedAt: z.number().int().positive(),
    reason: z.string().trim().min(1).max(2000),
    classification: z.object({
        schemaVersion: z.literal("mlx.public-data-classification.v1"),
        reviewedBy: z.string().trim().min(1).max(200),
        reviewedAt: z.number().int().positive(),
        evidenceRefs: z.array(z.string().trim().min(1).max(500)).min(1).max(40),
        fields: z.array(z.object({
            field: sqlIdentifierSchema,
            classification: z.enum(["aggregate_metric", "non_personal_dimension"]),
        }).strict()).max(160),
    }).strict(),
    navigation: z.object({
        channelLinks: z.literal(true),
    }).strict().optional(),
}).strict().superRefine((access, context) => {
    if (access.classification.reviewedAt > access.approvedAt) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["classification", "reviewedAt"],
            message: "Public data classification must be reviewed before approval.",
        });
    }
    const fields = access.classification.fields.map((item) => item.field);
    if (new Set(fields).size !== fields.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["classification", "fields"],
            message: "Public data classification fields must be unique.",
        });
    }
});
export const twinProductContractSchema = z.object({
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(2000),
    semanticKeys: z.array(z.string().trim().regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)+$/u)).min(1).max(20),
    canonicalDefinition: z.string().trim().min(1).max(8000),
    grain: z.string().trim().min(1).max(1000),
    intents: z.array(z.string().trim().min(1).max(240)).min(1).max(64),
    unsupportedIntents: z.array(z.string().trim().min(1).max(240)).max(64),
    routingBindings: z.array(twinProductRoutingBindingSchema).min(1).max(32),
    compositionPlans: z.array(twinProductCompositionPlanSchema).max(4).optional(),
    providerCompletions: z.array(twinProductProviderCompletionSchema).max(16).optional(),
    supersedesSkillSlugs: z.array(authoringSlugSchema).max(64).optional(),
    dimensions: z.array(sqlIdentifierSchema).max(80),
    measures: z.array(sqlIdentifierSchema).max(80),
    limitations: z.array(z.string().trim().min(1).max(1000)).max(64),
    sourceRefs: z.array(z.string().trim().min(1).max(240)).min(1).max(80),
    coverage: twinProductCoverageSchema.optional(),
    authorship: z.object({
        mode: z.enum(["automatic", "guided", "explicit"]),
        guidanceRefs: z.array(z.string().trim().min(1).max(500)).max(80),
    }).strict(),
    evaluation: twinProductEvaluationContractSchema.optional(),
    publicAccess: twinPublicAccessSchema.optional(),
}).strict().superRefine((contract, context) => {
    const owned = new Set(contract.routingBindings.map((binding) => binding.skillSlug));
    if (contract.publicAccess) {
        const classifications = new Map(contract.publicAccess.classification.fields.map((item) => [item.field, item.classification]));
        for (const dimension of contract.dimensions) {
            if (classifications.get(dimension) !== "non_personal_dimension") {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["publicAccess", "classification", "fields"],
                    message: `Public dimension ${dimension} requires non-personal classification evidence.`,
                });
            }
        }
        for (const measure of contract.measures) {
            if (classifications.get(measure) !== "aggregate_metric") {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["publicAccess", "classification", "fields"],
                    message: `Public measure ${measure} requires aggregate classification evidence.`,
                });
            }
        }
        const governedFields = new Set([...contract.dimensions, ...contract.measures]);
        for (const field of classifications.keys()) {
            if (!governedFields.has(field)) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["publicAccess", "classification", "fields"],
                    message: `Public classification field ${field} is not a governed output field.`,
                });
            }
        }
    }
    const planSlugs = new Set();
    for (const [index, plan] of (contract.compositionPlans ?? []).entries()) {
        if (!owned.has(plan.carrierSkillSlug)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["compositionPlans", index, "carrierSkillSlug"],
                message: `Composition carrier ${plan.carrierSkillSlug} is not owned by this product.`,
            });
        }
        if (owned.has(plan.slug)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["compositionPlans", index, "slug"],
                message: `Composition plan ${plan.slug} must not collide with a product skill slug.`,
            });
        }
        if (planSlugs.has(plan.slug)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["compositionPlans", index, "slug"],
                message: `Duplicate composition plan ${plan.slug}.`,
            });
        }
        planSlugs.add(plan.slug);
    }
    for (const [index, slug] of (contract.supersedesSkillSlugs ?? []).entries()) {
        if (owned.has(slug)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["supersedesSkillSlugs", index],
                message: `A Twin product cannot supersede its own routing skill ${slug}.`,
            });
        }
    }
});
export const twinProductValidationSchema = z.object({
    status: z.literal("passed"),
    mode: z.enum(["exact", "containment", "tolerance", "reviewed"]),
    checkedAt: z.number().int().positive(),
    candidateRowCount: z.number().int().positive(),
    groundTruthRowCount: z.number().int().nonnegative().optional(),
    mismatchCount: z.number().int().nonnegative().optional(),
    qualityIssueCount: z.literal(0),
    comparedColumns: z.array(sqlIdentifierSchema).optional(),
    maxRelativeDelta: z.number().nonnegative().optional(),
    tolerance: z.number().nonnegative().optional(),
    reviewedBy: z.string().trim().min(1).max(240).optional(),
    calibration: z.object({
        independence: z.enum(["independent_recompute", "human_curated_adoption", "shared_implementation"]),
        sourceRefs: z.array(z.string().trim().min(1).max(500)).min(1).max(32),
        note: z.string().trim().min(1).max(2000).optional(),
    }).strict().optional(),
    evidence: jsonObjectSchema,
}).strict();
export const twinProductCompiledSkillSchema = z.object({
    slug: authoringSlugSchema,
    expectedCurrentPublishedVersionId: authoringPersistedIdSchema.nullable().optional(),
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(1000).optional(),
    routingDescription: z.string().trim().max(600).optional(),
    operationalGuidance: z.string().trim().max(8000).default(""),
}).strict();
export const twinProductPublicationBundleSchema = z.object({
    slug: authoringSlugSchema,
    version: z.number().int().positive(),
    publishedRelationName: sqlIdentifierSchema,
    replacesPublishedRelationName: sqlIdentifierSchema.optional(),
    contract: twinProductContractSchema,
    validation: twinProductValidationSchema,
    skills: z.array(twinProductCompiledSkillSchema).min(1).max(16),
    channel: createChannelDataProductPatchSchema.optional(),
}).strict();
export const publishDerivedRelationProposalPatchSchema = z.object({
    derivedRelationId: authoringPersistedIdSchema,
    status: z.enum(["active", "archived"]).default("active"),
    product: twinProductPublicationBundleSchema.optional(),
}).strict();
export const createDerivedRelationProposalPatchSchema = z.object({
    id: authoringPersistedIdSchema.optional(),
    slug: authoringSlugSchema,
    version: z.number().int().positive(),
    status: z.enum(["draft", "active"]).default("active"),
    narrative: z.string().trim().min(1).max(8000),
    definition: jsonObjectSchema,
    lineage: jsonObjectSchema.default({}),
    relationSchema: z.enum(["mlx_curated", "connector_warehouse"]),
    relationName: sqlIdentifierSchema,
    compiledSql: z.string().trim().min(1).max(80_000),
    sourceWatermark: z.number().int().nonnegative().nullable().optional(),
    examples: z.array(z.unknown()).max(25).default([]),
    stats: jsonObjectSchema.default({}),
}).strict();
export const createProcessDefinitionProposalPatchSchema = z.object({
    id: authoringPersistedIdSchema.optional(),
    slug: authoringSlugSchema,
    version: z.number().int().positive(),
    status: twinDefinitionStatusSchema.default("hypothesis"),
    definition: jsonObjectSchema,
}).strict();
export const publishProcessDefinitionProposalPatchSchema = z.object({
    processDefinitionId: authoringPersistedIdSchema,
    status: twinDefinitionStatusSchema.default("verified"),
}).strict();
export const semanticViewEvidenceMappingSchema = z.object({
    source: z.string().trim().min(1).max(200),
    relation: relationNameSchema.optional(),
    field: fieldPathSchema.optional(),
    notes: z.string().trim().min(1).max(1000),
});
export const semanticViewDefinitionSchema = z
    .object({
    summary: z.string().trim().min(1).max(2000),
    grain: semanticViewGrainSchema,
    primaryRelation: relationNameSchema.optional(),
    requiredFilters: z.array(semanticViewRequiredFilterSchema).max(32).default([]),
    measures: z.array(semanticViewMeasureSchema).max(64).default([]),
    dimensions: z.array(semanticViewDimensionSchema).max(64).default([]),
    evidenceMappings: z
        .array(semanticViewEvidenceMappingSchema)
        .max(64)
        .default([]),
    validationRules: z.array(boundedTextSchema).max(64).default([]),
    examples: z.array(boundedTextSchema).max(32).default([]),
    caveats: z.array(boundedTextSchema).max(32).default([]),
    metadata: jsonObjectSchema.default({}),
})
    .superRefine((definition, ctx) => {
    const seenFilterFields = new Set();
    const addIssue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    for (const [index, filter] of definition.requiredFilters.entries()) {
        if (seenFilterFields.has(filter.field)) {
            addIssue(["requiredFilters", index, "field"], "Required filter fields must be unique");
        }
        seenFilterFields.add(filter.field);
        if (["equals", "not_equals", "gte", "lte"].includes(filter.operator) &&
            filter.value === undefined) {
            addIssue(["requiredFilters", index, "value"], "Operator requires value");
        }
        if (["equals", "not_equals", "gte", "lte"].includes(filter.operator) &&
            filter.values !== undefined) {
            addIssue(["requiredFilters", index, "values"], "Operator does not accept values");
        }
        if (["in", "not_in", "between"].includes(filter.operator) &&
            filter.values === undefined) {
            addIssue(["requiredFilters", index, "values"], "Operator requires values");
        }
        if (["in", "not_in", "between"].includes(filter.operator) &&
            filter.value !== undefined) {
            addIssue(["requiredFilters", index, "value"], "Operator does not accept value");
        }
        if (filter.operator === "between" &&
            filter.values !== undefined &&
            filter.values.length !== 2) {
            addIssue(["requiredFilters", index, "values"], "Between filters require exactly two values");
        }
        if (["is_present", "is_absent"].includes(filter.operator) &&
            (filter.value !== undefined || filter.values !== undefined)) {
            addIssue(["requiredFilters", index], "Presence filters do not accept value or values");
        }
    }
});
export const skillViewBindingKindSchema = z.enum([
    "skill_requires_view", "skill_suggests_view", "plugin_selects_view",
    "task_explicit_view",
]);
export const skillViewBindingStatusSchema = z.enum(["active", "archived"]);
export const createConvergenceDomainInputSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(120),
    organisationId: z.string().trim().min(1).max(160).optional(),
    slug: authoringSlugSchema,
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1000).default(""),
    status: convergenceDomainStatusSchema.default("active"),
    metadata: jsonObjectSchema.default({}),
    createdBy: authoringCreatedBySchema.default("convergence-agent"),
});
export const createSemanticViewInputSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(120),
    organisationId: z.string().trim().min(1).max(160).optional(),
    domainId: authoringPersistedIdSchema,
    slug: authoringSlugSchema,
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1000).default(""),
    viewKind: semanticViewKindSchema,
    status: semanticViewStatusSchema.default("active"),
    metadata: jsonObjectSchema.default({}),
    createdBy: authoringCreatedBySchema.default("convergence-agent"),
});
export const createSemanticViewVersionInputSchema = z.object({
    semanticViewId: authoringPersistedIdSchema,
    title: z.string().trim().min(1).max(240),
    definition: semanticViewDefinitionSchema,
    contentMarkdown: z.string().min(1).optional(),
    sourceAmbientContextVersionIds: z
        .array(authoringPersistedIdSchema)
        .max(64)
        .default([]),
    sourceWarehouseRelations: z.array(relationNameSchema).max(64).default([]),
    contentHash: z.string().trim().min(1).max(160),
    status: semanticViewVersionStatusSchema.default("draft"),
    createdBy: authoringCreatedBySchema.default("convergence-agent"),
});
export const createSkillViewBindingInputSchema = z
    .object({
    tenantSlug: z.string().trim().min(1).max(120),
    organisationId: z.string().trim().min(1).max(160).optional(),
    bindingKind: skillViewBindingKindSchema,
    semanticViewId: authoringPersistedIdSchema,
    semanticViewVersionId: authoringPersistedIdSchema.optional(),
    skillId: authoringPersistedIdSchema.optional(),
    skillVersionId: authoringPersistedIdSchema.optional(),
    pluginSlug: authoringSlugSchema.optional(),
    capability: skillCapabilitySchema.optional(),
    taskShape: jsonObjectSchema.default({}),
    routingNotes: z.string().trim().max(2000).optional(),
    status: skillViewBindingStatusSchema.default("active"),
    metadata: jsonObjectSchema.default({}),
    createdBy: authoringCreatedBySchema.default("convergence-agent"),
})
    .superRefine((binding, ctx) => {
    const addIssue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    if (["skill_requires_view", "skill_suggests_view"].includes(binding.bindingKind) &&
        (binding.skillId === undefined || binding.skillVersionId === undefined)) {
        addIssue(["skillId"], "Skill bindings require skillId and skillVersionId");
    }
    if (["skill_requires_view", "skill_suggests_view"].includes(binding.bindingKind) &&
        binding.semanticViewVersionId === undefined) {
        addIssue(["semanticViewVersionId"], "Skill bindings require semanticViewVersionId");
    }
    if (binding.bindingKind === "plugin_selects_view" &&
        binding.pluginSlug === undefined) {
        addIssue(["pluginSlug"], "Plugin bindings require pluginSlug");
    }
});
export const convergenceProposalStatusSchema = z.enum([
    "proposed", "reviewing", "changes_requested", "approved", "rejected", "applied", "failed",
]);
export const convergenceProposalSourceKindSchema = z.enum([
    "ui_feedback", "developer", "minimization",
]);
export const convergenceProposalTargetKindSchema = z.enum([
    "domain", "semantic_view", "semantic_view_version", "skill_view_binding",
    "skill_version", "curated_warehouse_view", "agent_prompt_version", "skill",
    "channel_data_product", "channel_view", "relationship_rule",
    "derived_relation", "process_definition", "source_record_designation",
]);
export const convergenceProposalOperationSchema = z.enum([
    "create_domain", "update_domain", "create_semantic_view", "update_semantic_view",
    "create_semantic_view_version", "publish_semantic_view_version", "archive_semantic_view",
    "create_skill_view_binding", "update_skill_view_binding", "archive_skill_view_binding",
    "publish_skill_version", "replace_curated_warehouse_view", "archive_curated_warehouse_view",
    "publish_agent_prompt_version", "create_skill",
    "create_channel_data_product", "bind_curated_view_to_channel_dataset",
    "patch_channel_view_definition", "create_relationship_rule",
    "publish_relationship_rule", "create_derived_relation", "publish_derived_relation",
    "create_process_definition", "publish_process_definition",
    "set_source_record_designation", "no_change",
]);
// Reused verbatim from the skill-creation route's own input schema so the
// two can never drift apart (see createSkillRecord in
// twin-tenant/src/routes/skills.ts).
export const createSkillProposalPatchSchema = createSkillInputSchema;
export const publishSkillVersionPatchSchema = patchSkillVersionInputSchema
    .omit({ markdown: true })
    .extend({
    skillId: authoringPersistedIdSchema,
    baseSkillVersionId: authoringPersistedIdSchema,
    newContentMarkdown: z.string().min(1),
    // The base markdown the proposal edited from. When present, a stale base can
    // be auto-rebased via 3-way merge instead of bouncing to human review.
    baseContentMarkdown: z.string().optional(),
})
    .strict();
/**
 * Minimum length a generated skill body must reach to be a plausible
 * skill. Real HP skills run ~500-3500 chars; this floor only rejects stubs.
 */
export const MIN_SKILL_MARKDOWN_CHARS = 80;
// Self-referential, refusal, or placeholder fragments that generation may
// emit *as* the skill body when it cannot produce a real edit. None of these
// should ever be published as live skill guidance. Matched case-insensitively.
// Note: bare "placeholder" is deliberately absent — legitimate skill guidance can
// mention placeholders (e.g. hp-overture-artist-scope-policy: 'placeholders such
// as "?"'). Only the parenthesized stub form is a refusal signature.
const SKILL_MARKDOWN_PLACEHOLDER_MARKERS = [
    "master_edit_required",
    "need current full markdown",
    "to patch safely",
    "(placeholder",
    "todo:",
    "tktk",
    "<insert",
    "as an ai language model",
];
const GUARDRAIL_BLOCK_RE = /<!--\s*mlx-sql-guardrail\s*([\s\S]*?)\s*-->/giu;
export function extractSkillSqlGuardrails(markdown) {
    const guardrails = [];
    for (const match of markdown.matchAll(GUARDRAIL_BLOCK_RE)) {
        const raw = match[1]?.trim();
        if (!raw)
            continue;
        let parsed;
        try {
            parsed = JSON.parse(raw);
        }
        catch (error) {
            const message = error instanceof Error ? error.message : String(error);
            throw new Error(`Invalid mlx-sql-guardrail block: ${message}`);
        }
        if (Array.isArray(parsed)) {
            guardrails.push(...parsed.map(readGuardrail));
        }
        else {
            guardrails.push(readGuardrail(parsed));
        }
    }
    return guardrails;
}
function validateSkillSqlGuardrails(markdown) {
    try {
        for (const guardrail of extractSkillSqlGuardrails(markdown)) {
            const id = guardrail.id?.trim() || "unnamed";
            for (const pattern of [
                ...(guardrail.appliesSqlRegex ?? []),
                ...(guardrail.requiredSqlRegex ?? []),
                ...(guardrail.forbiddenSqlRegex ?? []),
                ...(guardrail.skipSqlRegex ?? []),
            ]) {
                try {
                    new RegExp(pattern, "iu");
                }
                catch (error) {
                    const message = error instanceof Error ? error.message : String(error);
                    throw new Error(`Invalid mlx-sql-guardrail regex for ${id}: ${message}`);
                }
            }
        }
    }
    catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return { ok: false, code: "invalid_sql_guardrail", reason: message };
    }
    return { ok: true };
}
function readGuardrail(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new Error("Invalid mlx-sql-guardrail block: expected object.");
    }
    const record = value;
    return {
        id: readOptionalString(record, "id"),
        promptAny: readOptionalStringArray(record, "promptAny"),
        promptAll: readOptionalStringArray(record, "promptAll"),
        promptNone: readOptionalStringArray(record, "promptNone"),
        appliesSqlRegex: readOptionalStringArray(record, "appliesSqlRegex"),
        requiredSqlIncludes: readOptionalStringArray(record, "requiredSqlIncludes"),
        requiredSqlRegex: readOptionalStringArray(record, "requiredSqlRegex"),
        forbiddenSqlIncludes: readOptionalStringArray(record, "forbiddenSqlIncludes"),
        forbiddenSqlRegex: readOptionalStringArray(record, "forbiddenSqlRegex"),
        skipSqlRegex: readOptionalStringArray(record, "skipSqlRegex"),
        failureMessage: readOptionalString(record, "failureMessage"),
    };
}
function readOptionalString(record, key) {
    const value = record[key];
    if (value === undefined || value === null)
        return undefined;
    if (typeof value !== "string") {
        throw new Error(`Invalid mlx-sql-guardrail block: ${key} must be a string.`);
    }
    return value;
}
function readOptionalStringArray(record, key) {
    const value = record[key];
    if (value === undefined || value === null)
        return undefined;
    if (!Array.isArray(value) || value.some((item) => typeof item !== "string")) {
        throw new Error(`Invalid mlx-sql-guardrail block: ${key} must be a string array.`);
    }
    return value;
}
/**
 * Guard against convergence publishing a degenerate skill body
 * (e.g. the "(MASTER_EDIT_REQUIRED: ...)" placeholder it emits when it lacks the
 * base markdown). Pure + dependency-free so both the proposal-creation route and
 * the apply gate can share it. Pass `baseMarkdown` (the current published body)
 * to additionally reject catastrophic shrink.
 */
export function validateSkillMarkdownContent(markdown, options = {}) {
    if (typeof markdown !== "string" || markdown.trim().length === 0) {
        return { ok: false, code: "empty", reason: "Skill markdown is empty." };
    }
    const trimmed = markdown.trim();
    const lower = trimmed.toLowerCase();
    for (const marker of SKILL_MARKDOWN_PLACEHOLDER_MARKERS) {
        if (lower.includes(marker)) {
            return {
                ok: false,
                code: "placeholder",
                reason: `Skill markdown contains placeholder/refusal marker "${marker}"; it is not a real skill body.`,
            };
        }
    }
    const guardrailCheck = validateSkillSqlGuardrails(trimmed);
    if (!guardrailCheck.ok)
        return guardrailCheck;
    if (trimmed.length < MIN_SKILL_MARKDOWN_CHARS) {
        return {
            ok: false,
            code: "too_short",
            reason: `Skill markdown is ${trimmed.length} chars, below the ${MIN_SKILL_MARKDOWN_CHARS}-char minimum for a usable skill.`,
        };
    }
    const hasHeading = /^\s{0,3}#/mu.test(trimmed);
    const bulletCount = (trimmed.match(/^\s*[-*] /gmu) ?? []).length;
    if (!hasHeading && bulletCount < 2) {
        return {
            ok: false,
            code: "unstructured",
            reason: "Skill markdown has no heading and fewer than two guidance bullets; it does not look like skill guidance.",
        };
    }
    const base = typeof options.baseMarkdown === "string" ? options.baseMarkdown.trim() : "";
    if (base.length >= 200 && trimmed.length < Math.ceil(base.length * 0.5)) {
        return {
            ok: false,
            code: "catastrophic_shrink",
            reason: `Skill markdown shrank from ${base.length} to ${trimmed.length} chars (>50% reduction); likely a truncated or degenerate edit.`,
        };
    }
    return { ok: true };
}
export const publishSemanticViewVersionPatchSchema = z.object({
    expectedCurrentSemanticViewVersionId: authoringPersistedIdSchema.nullable(),
}).strict();
export const curatedMaterializedIndexSchema = z.object({
    columns: z.array(sqlIdentifierSchema).min(1).max(8),
    unique: z.boolean().default(false),
}).strict().superRefine((index, ctx) => {
    if (new Set(index.columns).size !== index.columns.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["columns"],
            message: "Materialized index columns must be unique",
        });
    }
});
const materializedIndexesSchema = z.array(curatedMaterializedIndexSchema).max(8).default([]);
export const curatedWarehouseViewPatchSchema = z.object({
    semanticViewVersionId: authoringPersistedIdSchema,
    viewName: sqlIdentifierSchema,
    sql: z.string().trim().min(1).max(80_000),
    storageKind: z.enum(["view", "materialized_view"]).default("view"),
    materializedIndexes: materializedIndexesSchema,
}).strict().superRefine(assertMaterializedIndexes);
export const generatedCuratedSemanticViewPatchSchema = z.object({
    domainId: authoringPersistedIdSchema,
    domainSlug: authoringSlugSchema,
    domainName: z.string().trim().min(1).max(160),
    domainDescription: z.string().trim().min(1).max(1000),
    semanticViewId: authoringPersistedIdSchema,
    semanticViewVersionId: authoringPersistedIdSchema,
    slug: authoringSlugSchema,
    name: z.string().trim().min(1).max(160),
    description: z.string().trim().min(1).max(1000),
    version: z.number().int().positive(),
    definition: jsonObjectSchema,
    contentMarkdown: z.string().trim().min(1).max(80_000),
    sourceWarehouseRelations: z.array(relationNameSchema).max(80).default([]),
    contentHash: z.string().trim().min(1).max(256),
}).strict();
export const curatedWarehouseViewProposalPatchSchema = z.object({
    semanticViewVersionId: authoringPersistedIdSchema.optional(),
    semanticView: generatedCuratedSemanticViewPatchSchema.optional(),
    viewName: sqlIdentifierSchema,
    sql: z.string().trim().min(1).max(80_000),
    storageKind: z.enum(["view", "materialized_view"]).default("view"),
    materializedIndexes: materializedIndexesSchema,
}).strict().superRefine((patch, ctx) => {
    assertMaterializedIndexes(patch, ctx);
    if (!patch.semanticViewVersionId && !patch.semanticView) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["semanticViewVersionId"],
            message: "Curated view proposals require semanticViewVersionId or semanticView",
        });
    }
});
function assertMaterializedIndexes(patch, ctx) {
    if (patch.storageKind !== "materialized_view" && patch.materializedIndexes.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["materializedIndexes"],
            message: "Materialized indexes require storageKind materialized_view",
        });
    }
    const signatures = patch.materializedIndexes.map((index) => `${index.unique ? "unique" : "index"}:${index.columns.join(",")}`);
    if (new Set(signatures).size !== signatures.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["materializedIndexes"],
            message: "Materialized index definitions must be unique",
        });
    }
}
export const publishAgentPromptVersionPatchSchema = z.object({
    agentPromptId: authoringPersistedIdSchema,
    baseAgentPromptVersionId: authoringPersistedIdSchema,
    newBody: z.string().trim().min(1).max(80_000),
    variablesJson: z.array(z.unknown()).default([]),
}).strict();
export const convergenceQuestionOwnerSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("apply"), proposalId: authoringPersistedIdSchema }),
    z.object({ kind: z.literal("none") }),
]);
export const createConvergenceProposalInputSchema = z.object({
    tenantSlug: z.string().trim().min(1).max(120),
    organisationId: z.string().trim().min(1).max(160).optional(),
    sourceSessionId: authoringPersistedIdSchema.optional(),
    sourceRunId: authoringPersistedIdSchema.optional(),
    sourceTaskId: authoringPersistedIdSchema.optional(),
    sourceKind: convergenceProposalSourceKindSchema,
    targetKind: convergenceProposalTargetKindSchema,
    operation: convergenceProposalOperationSchema,
    targetDomainId: authoringPersistedIdSchema.optional(),
    targetSemanticViewId: authoringPersistedIdSchema.optional(),
    targetSemanticViewVersionId: authoringPersistedIdSchema.optional(),
    targetSkillViewBindingId: authoringPersistedIdSchema.optional(),
    targetSkillId: authoringPersistedIdSchema.optional(),
    targetSkillVersionId: authoringPersistedIdSchema.optional(),
    targetCuratedViewId: authoringPersistedIdSchema.optional(),
    targetAgentPromptId: authoringPersistedIdSchema.optional(),
    targetAgentPromptVersionId: authoringPersistedIdSchema.optional(),
    targetChannelId: authoringPersistedIdSchema.optional(),
    targetChannelViewId: authoringPersistedIdSchema.optional(),
    title: z.string().trim().min(1).max(240),
    rationale: z.string().trim().min(1).max(8000),
    evidence: jsonObjectSchema.default({}),
    proposedPatch: jsonObjectSchema.default({}),
    createdBy: authoringCreatedBySchema.default("convergence-agent"),
    // Optional stable identifier for the underlying gap this proposal addresses.
    // When present, open proposals for the same gap converge onto one row instead
    // of churning into divergent content-hashed proposals.
    gapKey: z.string().trim().min(1).max(200).optional(),
    // Plain-language, no-diff summary of the change for the task owner, e.g.
    // "Now treats credit notes as negative invoice totals."
    userSummary: z.string().trim().min(1).max(600).optional(),
}).superRefine((proposal, ctx) => {
    const addIssue = (path, message) => ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });
    const expectedTargetKindByOperation = {
        create_domain: "domain",
        update_domain: "domain",
        create_semantic_view: "semantic_view",
        update_semantic_view: "semantic_view",
        create_semantic_view_version: "semantic_view_version",
        publish_semantic_view_version: "semantic_view_version",
        archive_semantic_view: "semantic_view",
        create_skill_view_binding: "skill_view_binding",
        update_skill_view_binding: "skill_view_binding",
        archive_skill_view_binding: "skill_view_binding",
        publish_skill_version: "skill_version",
        replace_curated_warehouse_view: "curated_warehouse_view",
        archive_curated_warehouse_view: "curated_warehouse_view",
        publish_agent_prompt_version: "agent_prompt_version",
        create_skill: "skill",
        create_channel_data_product: "channel_data_product",
        bind_curated_view_to_channel_dataset: "channel_data_product",
        patch_channel_view_definition: "channel_view",
        create_relationship_rule: "relationship_rule",
        publish_relationship_rule: "relationship_rule",
        create_derived_relation: "derived_relation",
        publish_derived_relation: "derived_relation",
        create_process_definition: "process_definition",
        publish_process_definition: "process_definition",
        set_source_record_designation: "source_record_designation",
    };
    const expectedTargetKind = expectedTargetKindByOperation[proposal.operation];
    if (expectedTargetKind !== undefined &&
        proposal.targetKind !== expectedTargetKind) {
        addIssue(["targetKind"], `Operation requires targetKind ${expectedTargetKind}`);
    }
    const requireField = (operation, field) => {
        if (proposal.operation === operation && proposal[field] === undefined) {
            addIssue([field], `${operation} requires ${field}`);
        }
    };
    requireField("update_domain", "targetDomainId");
    requireField("create_semantic_view", "targetDomainId");
    requireField("update_semantic_view", "targetSemanticViewId");
    requireField("archive_semantic_view", "targetSemanticViewId");
    requireField("create_semantic_view_version", "targetSemanticViewId");
    requireField("publish_semantic_view_version", "targetSemanticViewVersionId");
    requireField("update_skill_view_binding", "targetSkillViewBindingId");
    requireField("archive_skill_view_binding", "targetSkillViewBindingId");
    requireField("publish_skill_version", "targetSkillId");
    requireField("publish_skill_version", "targetSkillVersionId");
    requireField("archive_curated_warehouse_view", "targetCuratedViewId");
    requireField("publish_agent_prompt_version", "targetAgentPromptId");
    requireField("publish_agent_prompt_version", "targetAgentPromptVersionId");
    requireField("bind_curated_view_to_channel_dataset", "targetChannelId");
    requireField("patch_channel_view_definition", "targetChannelViewId");
    if ((proposal.operation === "create_relationship_rule" ||
        proposal.operation === "publish_relationship_rule" ||
        proposal.operation === "create_derived_relation" ||
        proposal.operation === "publish_derived_relation" ||
        proposal.operation === "create_process_definition" ||
        proposal.operation === "publish_process_definition" ||
        proposal.operation === "set_source_record_designation") &&
        proposal.organisationId === undefined) {
        addIssue(["organisationId"], `${proposal.operation} requires organisationId`);
    }
    if (proposal.operation === "create_skill_view_binding" &&
        proposal.targetSemanticViewId === undefined &&
        proposal.targetSemanticViewVersionId === undefined) {
        addIssue(["targetSemanticViewId"], "create_skill_view_binding requires targetSemanticViewId or targetSemanticViewVersionId");
    }
    if (proposal.operation === "replace_curated_warehouse_view" &&
        proposal.targetSemanticViewVersionId === undefined &&
        proposal.targetCuratedViewId === undefined &&
        !("semanticView" in proposal.proposedPatch)) {
        addIssue(["targetSemanticViewVersionId"], "replace_curated_warehouse_view requires targetSemanticViewVersionId, targetCuratedViewId, or proposedPatch.semanticView");
    }
    if ((proposal.operation === "replace_curated_warehouse_view" || proposal.operation === "archive_curated_warehouse_view") &&
        proposal.organisationId === undefined) {
        addIssue(["organisationId"], `${proposal.operation} requires organisationId`);
    }
    if (proposal.operation === "publish_skill_version") {
        if ("skillId" in proposal.proposedPatch && proposal.proposedPatch.skillId !== proposal.targetSkillId) {
            addIssue(["proposedPatch", "skillId"], "proposedPatch.skillId must match targetSkillId");
        }
        if ("baseSkillVersionId" in proposal.proposedPatch &&
            proposal.proposedPatch.baseSkillVersionId !== proposal.targetSkillVersionId) {
            addIssue(["proposedPatch", "baseSkillVersionId"], "proposedPatch.baseSkillVersionId must match targetSkillVersionId");
        }
        const parsed = publishSkillVersionPatchSchema.safeParse({
            ...proposal.proposedPatch,
            skillId: proposal.targetSkillId,
            baseSkillVersionId: proposal.targetSkillVersionId,
        });
        if (!parsed.success) {
            addIssue(["proposedPatch"], "publish_skill_version requires a valid skill-version patch");
        }
    }
    if (proposal.operation === "publish_agent_prompt_version") {
        if ("agentPromptId" in proposal.proposedPatch && proposal.proposedPatch.agentPromptId !== proposal.targetAgentPromptId) {
            addIssue(["proposedPatch", "agentPromptId"], "proposedPatch.agentPromptId must match targetAgentPromptId");
        }
        if ("baseAgentPromptVersionId" in proposal.proposedPatch &&
            proposal.proposedPatch.baseAgentPromptVersionId !== proposal.targetAgentPromptVersionId) {
            addIssue(["proposedPatch", "baseAgentPromptVersionId"], "proposedPatch.baseAgentPromptVersionId must match targetAgentPromptVersionId");
        }
        const parsed = publishAgentPromptVersionPatchSchema.safeParse({
            ...proposal.proposedPatch,
            agentPromptId: proposal.targetAgentPromptId,
            baseAgentPromptVersionId: proposal.targetAgentPromptVersionId,
        });
        if (!parsed.success) {
            addIssue(["proposedPatch"], "publish_agent_prompt_version requires a valid agent-prompt-version patch");
        }
    }
    if (proposal.operation === "publish_semantic_view_version") {
        const parsed = publishSemanticViewVersionPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "publish_semantic_view_version requires expectedCurrentSemanticViewVersionId");
        }
    }
    if (proposal.operation === "replace_curated_warehouse_view") {
        if (proposal.targetSemanticViewVersionId !== undefined &&
            typeof proposal.proposedPatch.semanticViewVersionId === "string" &&
            proposal.proposedPatch.semanticViewVersionId !== proposal.targetSemanticViewVersionId) {
            addIssue(["proposedPatch", "semanticViewVersionId"], "proposedPatch.semanticViewVersionId must match targetSemanticViewVersionId");
        }
        if (typeof proposal.proposedPatch.sql !== "string" || proposal.proposedPatch.sql.trim().length === 0) {
            addIssue(["proposedPatch", "sql"], "replace_curated_warehouse_view requires proposedPatch.sql");
        }
        const parsed = curatedWarehouseViewProposalPatchSchema.safeParse({
            ...proposal.proposedPatch,
            semanticViewVersionId: proposal.targetSemanticViewVersionId ?? proposal.proposedPatch.semanticViewVersionId,
        });
        if (!parsed.success) {
            addIssue(["proposedPatch"], "replace_curated_warehouse_view requires a valid curated view patch");
        }
    }
    if (proposal.operation === "create_skill") {
        const parsed = createSkillProposalPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "create_skill requires a valid skill-creation patch");
        }
    }
    if (proposal.operation === "create_channel_data_product") {
        const parsed = createChannelDataProductPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "create_channel_data_product requires a valid channel data-product patch");
        }
    }
    if (proposal.operation === "bind_curated_view_to_channel_dataset") {
        const parsed = bindCuratedViewToChannelDatasetPatchSchema.safeParse({
            ...proposal.proposedPatch,
            channelId: proposal.targetChannelId,
        });
        if (!parsed.success) {
            addIssue(["proposedPatch"], "bind_curated_view_to_channel_dataset requires a valid dataset-binding patch");
        }
    }
    if (proposal.operation === "patch_channel_view_definition") {
        const parsed = patchChannelViewDefinitionProposalPatchSchema.safeParse({
            ...proposal.proposedPatch,
            viewId: proposal.targetChannelViewId,
        });
        if (!parsed.success) {
            addIssue(["proposedPatch"], "patch_channel_view_definition requires a valid channel view patch");
        }
    }
    if (proposal.operation === "create_relationship_rule") {
        const parsed = createRelationshipRuleProposalPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "create_relationship_rule requires a valid relationship-rule patch");
        }
    }
    if (proposal.operation === "publish_relationship_rule") {
        const parsed = publishRelationshipRuleProposalPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "publish_relationship_rule requires relationshipRuleId");
        }
    }
    if (proposal.operation === "publish_derived_relation") {
        const parsed = publishDerivedRelationProposalPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "publish_derived_relation requires derivedRelationId");
        }
    }
    if (proposal.operation === "create_derived_relation") {
        const parsed = createDerivedRelationProposalPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "create_derived_relation requires a valid derived-relation patch");
        }
    }
    if (proposal.operation === "create_process_definition") {
        const parsed = createProcessDefinitionProposalPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "create_process_definition requires a valid process-definition patch");
        }
    }
    if (proposal.operation === "publish_process_definition") {
        const parsed = publishProcessDefinitionProposalPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "publish_process_definition requires processDefinitionId");
        }
    }
    if (proposal.operation === "set_source_record_designation") {
        const parsed = setSourceRecordDesignationProposalPatchSchema.safeParse(proposal.proposedPatch);
        if (!parsed.success) {
            addIssue(["proposedPatch"], "set_source_record_designation requires a valid source designation patch");
        }
        if (Object.keys(proposal.evidence).length === 0) {
            addIssue(["evidence"], "set_source_record_designation requires review evidence");
        }
    }
});
export const convergenceProposalEventKindSchema = z.enum([
    "proposal.created", "proposal.status_changed", "proposal.review_requested",
    "proposal.approved", "proposal.rejected", "proposal.changes_requested",
    "proposal.auto_approved", "proposal.evidence_recorded",
    "view.version_created", "view.version_published", "binding.created", "binding.updated",
    "product.version_staged",
    "gate.started", "gate.passed", "gate.failed",
    "apply.started", "apply.completed", "apply.failed", "apply.retry_requested",
    "apply.manual_override", "skill.stale_base", "skill.invalid_content",
    "curated_view.dependent_breakage",
    "agent_prompt.stale_base", "question.raised", "question.resolved",
    "skill.auto_rebased", "minimization.detected",
]);
export const convergenceProposalEventInputSchema = z.object({
    proposalId: authoringPersistedIdSchema,
    kind: convergenceProposalEventKindSchema,
    payload: jsonObjectSchema.default({}),
    actorId: z.string().trim().min(1).max(160).optional(),
});
export const convergenceProposalTransitionSchema = z.enum([
    "start_review",
    "request_changes",
    "approve",
    "reject",
    "mark_failed",
]);
export const convergenceProposalTransitionInputSchema = z
    .object({
    transition: convergenceProposalTransitionSchema,
    note: z.string().trim().min(1).max(4000).optional(),
    failureMessage: z.string().trim().min(1).max(4000).optional(),
})
    .superRefine((value, ctx) => {
    if (["request_changes", "reject", "mark_failed"].includes(value.transition) &&
        !value.note &&
        !value.failureMessage) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["note"],
            message: `${value.transition} requires a note`,
        });
    }
});
export const convergenceProposalBatchActionSchema = z.enum([
    "approve",
    "reject",
    "adopt_to_org",
]);
export const convergenceProposalBatchInputSchema = z
    .object({
    proposalIds: z.array(authoringPersistedIdSchema).min(1).max(100),
    action: convergenceProposalBatchActionSchema,
    organisationId: z.string().trim().min(1).max(160).optional(),
    note: z.string().trim().min(1).max(4000).optional(),
})
    .superRefine((value, ctx) => {
    if (value.action === "reject" && !value.note) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["note"],
            message: "reject requires a note",
        });
    }
});
export const convergenceQuestionResolveInputSchema = z.object({
    resolution: z.string().trim().min(1).max(4000),
    resolverPayload: jsonObjectSchema.default({}),
});
export const convergenceApplyJobStatusSchema = z.enum([
    "queued", "running", "completed", "failed",
]);
export const coverageMatrixEntrySchema = z.object({
    source: z.string(),
    entity: z.string(),
    rawEventCount: z.number(),
    viewCount: z.number(),
    publishedVersionCount: z.number(),
    bindingCount: z.number(),
    coverageScore: z.number(),
    lastSeenAt: z.number().nullable(),
});
export const proposalFunnelBucketSchema = z.object({
    status: convergenceProposalStatusSchema,
    count: z.number(),
    medianAgeMs: z.number().nullable(),
});
export const proposalFunnelSchema = z.object({
    windowDays: z.number(),
    totals: z.array(proposalFunnelBucketSchema),
    bySourceKind: z.array(z.object({
        sourceKind: convergenceProposalSourceKindSchema,
        count: z.number(),
    })),
});
export const userQueryClusterSchema = z.object({
    signature: z.string(),
    exampleTitle: z.string(),
    exampleTaskId: z.string(),
    promptKeywords: z.array(z.string()),
    taskCount: z.number(),
    runCount: z.number(),
    proposalCount: z.number(),
    bindingCount: z.number(),
    lastSeenAt: z.number().nullable(),
});
